import { useCallback, useEffect, useRef, useState } from 'react';
import userSettingsService from '../../../services/userSettings.service';

/** The single per-user setting key holding every card's font scale. */
const SETTING_KEY = 'quote_drawer_card_scales';

/** Discrete zoom steps shared with the Dev Activity card control. Default = 1. */
export const FONT_SCALES = [0.5, 0.75, 1, 1.25, 1.5, 1.75, 2, 2.5, 3];

/** Move one slot up (+1) or down (-1) the discrete scale array, clamped at the
 *  ends. An off-array `current` snaps to 100%'s neighbours. */
export function stepFontScale(current: number, direction: 1 | -1): number {
  const idx = FONT_SCALES.findIndex((s) => Math.abs(s - current) < 0.01);
  const base = idx === -1 ? FONT_SCALES.indexOf(1) : idx;
  const next = Math.min(Math.max(base + direction, 0), FONT_SCALES.length - 1);
  return FONT_SCALES[next];
}

/** Map of card identity → font scale (e.g. { billTo: 1.25 }). Only cards the
 *  user has changed appear; everything else defaults to 1 (100%). */
export type CardScaleMap = Record<string, number>;

/** Coerce an unknown stored/loaded value into a well-formed scale map, keeping
 *  only positive finite numbers. */
function normalize(value: unknown): CardScaleMap {
  const out: CardScaleMap = {};
  if (value && typeof value === 'object') {
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      const n = typeof v === 'number' ? v : Number(v);
      if (Number.isFinite(n) && n > 0) out[k] = n;
    }
  }
  return out;
}

function readCache(): CardScaleMap | null {
  try {
    const raw = localStorage.getItem(userSettingsService.getCacheKey(SETTING_KEY));
    return raw ? normalize(JSON.parse(raw)) : null;
  } catch {
    return null;
  }
}

function writeCache(state: CardScaleMap): void {
  try {
    localStorage.setItem(userSettingsService.getCacheKey(SETTING_KEY), JSON.stringify(state));
  } catch {
    /* quota / unavailable — ignore; the server row stays the source of truth */
  }
}

/** isAuthenticated() reaches into MSAL (and its localStorage); treat any failure
 *  as "not authenticated" so the scales still work locally and never crash the
 *  host page when MSAL storage is unavailable. */
function isAuthed(): boolean {
  try {
    return userSettingsService.isAuthenticated();
  } catch {
    return false;
  }
}

/**
 * Remembers each Quote-drawer card's font scale — per user, across reloads AND
 * across opening different quotes (the map is global, keyed by card identity,
 * not by quote). Consumed via QuoteCardScaleProvider; each `Card` reads its own
 * entry by `scaleKey`.
 *
 * State is layered for a correct-yet-instant experience (mirrors
 * useQuoteSectionCollapse):
 *   1. synchronous localStorage read → correct first paint (no 100%→scaled flash)
 *   2. server reconcile on mount → the user_settings row is the source of truth
 *   3. explicit setScale writes through to localStorage + server, MERGED — so a
 *      card change never clobbers another card's saved scale
 *
 * The server write is skipped while the initial load is in a FAILED state, so a
 * stale/default value can't overwrite real settings when the backend is briefly
 * unreachable. Writes fire only on an explicit setScale, never on load; and a
 * slow load never overwrites a change the user already made on this mount.
 */
export default function useQuoteCardScales(): {
  scales: CardScaleMap;
  setScale: (key: string, value: number) => void;
} {
  const [scales, setScales] = useState<CardScaleMap>(() => readCache() ?? {});
  const scalesRef = useRef(scales);
  scalesRef.current = scales;

  // Set true only on a real load error (a 404 → null is "no setting yet", not a
  // failure). Blocks the server write so we never push a stale value over a row
  // we couldn't read. The user change still updates state + the local cache.
  const loadFailedRef = useRef(false);
  // Once the user has changed a scale this mount, their live action is
  // authoritative — a late server response must not revert it.
  const userTouchedRef = useRef(false);

  useEffect(() => {
    if (!isAuthed()) return;
    let cancelled = false;
    const controller = new AbortController();
    userSettingsService
      .getUserSetting(SETTING_KEY, controller.signal)
      .then((setting) => {
        if (cancelled || userTouchedRef.current) return;
        // null => no saved setting (404); keep the cached/default state.
        if (setting && setting.setting_value != null) {
          const next = normalize(setting.setting_value);
          scalesRef.current = next;
          setScales(next);
          writeCache(next);
        }
      })
      .catch(() => {
        if (cancelled || controller.signal.aborted) return;
        loadFailedRef.current = true;
      });
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, []);

  const setScale = useCallback((key: string, value: number) => {
    userTouchedRef.current = true;
    const next: CardScaleMap = { ...scalesRef.current, [key]: value };
    scalesRef.current = next;
    setScales(next);
    writeCache(next);
    // Best-effort server persist — only on an explicit change, and only when the
    // initial load didn't fail (else we'd risk clobbering an unread row).
    if (!loadFailedRef.current && isAuthed()) {
      userSettingsService.saveUserSetting(SETTING_KEY, next).catch(() => {
        /* best-effort — localStorage keeps the value for this browser */
      });
    }
  }, []);

  return { scales, setScale };
}
